import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { renderChatMessageContent } from '../utils/renderChatMessageContent';
import './ProjectInquiry.css';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

interface BoxMcpStatus {
  configured: boolean;
  folderId: string | null;
  folderName: string | null;
  serverUrl: string | null;
  serverLabel: string | null;
  authMethod: string;
  connected: boolean;
  oauthAuthorizeUrl: string | null;
}

function createMessageId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function ProjectInquiry() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [boxMcpStatus, setBoxMcpStatus] = useState<BoxMcpStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  const scrollToBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/project-inquiry/status`);
        const data = await response.json();
        if (!response.ok || data.success !== true || !data.boxMcp) {
          throw new Error(data.error || 'Failed to load Box MCP status.');
        }
        setBoxMcpStatus(data.boxMcp as BoxMcpStatus);
        setStatusError(null);
      } catch (err) {
        setStatusError(err instanceof Error ? err.message : 'Failed to load Box MCP status.');
      }
    };

    void loadStatus();
  }, []);

  const sendMessage = async () => {
    const text = draft.trim();
    if (!text || isLoading) return;

    const userMessage: ChatMessage = {
      id: createMessageId(),
      role: 'user',
      content: text,
    };

    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setDraft('');
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/project-inquiry/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: nextMessages.map(({ role, content }) => ({ role, content })),
        }),
      });

      const data = await response.json();
      if (!response.ok || data.success !== true || typeof data.message !== 'string') {
        throw new Error(data.error || 'Failed to get a response.');
      }

      setMessages((prev) => [
        ...prev,
        {
          id: createMessageId(),
          role: 'assistant',
          content: data.message,
        },
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void sendMessage();
    }
  };

  const boxFolderLabel =
    boxMcpStatus?.folderName && boxMcpStatus.folderId
      ? `${boxMcpStatus.folderName} (${boxMcpStatus.folderId})`
      : boxMcpStatus?.folderId ?? null;

  const needsOAuthConnect =
    boxMcpStatus?.configured === true &&
    boxMcpStatus.connected === false &&
    Boolean(boxMcpStatus.oauthAuthorizeUrl);

  const emptyStateMessage =
    boxMcpStatus?.configured && boxFolderLabel
      ? `Ask a question about the project data room in Box folder ${boxFolderLabel}.`
      : 'Ask a question about a Soteria project, loan, or data room.';

  return (
    <div className="ProjectInquiry">
      <header className="ProjectInquiry-header">
        <Link to="/" className="back-link">
          <img
            src="https://app.soteriamarket.com/banner3.jpeg"
            alt="Soteria Logo"
            className="logo"
          />
        </Link>
        <h1>Project Inquiry</h1>
      </header>

      <main className="ProjectInquiry-main">
        <section className="project-inquiry-panel" aria-label="Project inquiry chat">
          {(boxMcpStatus || statusError) && (
            <div className="project-inquiry-box-status">
              {statusError && (
                <p className="project-inquiry-box-status-error">{statusError}</p>
              )}
              {boxMcpStatus?.configured && boxFolderLabel && (
                <p className="project-inquiry-box-status-folder">
                  Box folder: <strong>{boxFolderLabel}</strong>
                </p>
              )}
              {boxMcpStatus?.configured && !statusError && (
                <p className="project-inquiry-box-status-auth">
                  {boxMcpStatus.connected
                    ? `Connected via ${boxMcpStatus.authMethod}. Questions use Box MCP + OpenAI.`
                    : 'Box MCP is configured but not connected yet.'}
                </p>
              )}
              {needsOAuthConnect && boxMcpStatus.oauthAuthorizeUrl && (
                <a
                  className="project-inquiry-box-connect"
                  href={boxMcpStatus.oauthAuthorizeUrl}
                >
                  Connect Box account
                </a>
              )}
            </div>
          )}

          <div className="project-inquiry-chat">
            <div
              ref={messagesContainerRef}
              className="project-inquiry-chat-messages"
              role="log"
              aria-live="polite"
              aria-relevant="additions"
            >
              {messages.length === 0 && !isLoading && (
                <p className="project-inquiry-chat-empty">{emptyStateMessage}</p>
              )}

              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`project-inquiry-chat-message role-${message.role}`}
                >
                  <div className="project-inquiry-chat-message-label">
                    {message.role === 'user' ? 'You' : 'Assistant'}
                  </div>
                  <div className="project-inquiry-chat-message-body">
                    {message.role === 'assistant'
                      ? renderChatMessageContent(message.content)
                      : message.content}
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="project-inquiry-chat-message role-assistant">
                  <div className="project-inquiry-chat-message-label">Assistant</div>
                  <div className="project-inquiry-chat-message-body project-inquiry-chat-typing">
                    <span
                      className="project-inquiry-chat-progress"
                      aria-hidden="true"
                    />
                    <span>
                      {boxMcpStatus?.configured
                        ? 'Searching Box and drafting a reply…'
                        : 'Thinking…'}
                    </span>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <div className="project-inquiry-chat-composer">
              {error && <div className="project-inquiry-chat-error">{error}</div>}
              <div className="project-inquiry-chat-composer-row">
                <textarea
                  className="project-inquiry-chat-input"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your question…"
                  rows={3}
                  disabled={isLoading}
                  aria-label="Your message"
                />
                <button
                  type="button"
                  className="project-inquiry-chat-send"
                  onClick={() => void sendMessage()}
                  disabled={isLoading || draft.trim() === ''}
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default ProjectInquiry;
